import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { RootScopeService } from '../../../../core/shared-service/root-scope.service';
import { RootScopeDeclare } from '../../../../core/rootscope-declare';
import { Subscription } from 'rxjs';
import { FacilityService } from '../../../../core/services/facility.service';
import { FacilityObject } from 'src/app/core/services/facility.model';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { StaffService } from '../../../services/staff.service';
import {
  staffDetails,
  userPermissions,
  IStaffResponse,
  IStaffObject,
  staffInfo,
  staffActions,
} from '../../../services/staff.model';
import { HaloLoaderService } from '../../../../core/halo-loader/halo-loader.service';
import { HaloToastService } from 'src/app/core/halo-toast/halo-toast.service';
import { CommonUtil } from 'src/app/core/Utils/common-util';
import { HaloModalService } from 'src/app/core/halo-modal/halo-modal.service';
import { BreadcrumbService } from 'src/app/post-login/services/breadcrumb.service';
import { AmplitudeAnalyticsService } from 'src/app/core/services/amplitude-analytics.service';

@Component({
  selector: 'app-add-staff',
  templateUrl: './add-staff.component.html',
  styleUrls: ['./add-staff.component.scss'],
})
export class AddStaffComponent implements OnInit {
  rootScopeData: RootScopeDeclare;
  private _facilityOnChange: Subscription;
  currentFacility: FacilityObject;
  addStaffForm: FormGroup;
  emailAlreadyExists: boolean = false;
  isAdminChecked: boolean = false;
  submitted: boolean = false;
  _hideaction: boolean = true;
  _permissionData: userPermissions;
  _staffList: IStaffObject[];
  isShowDescription: boolean = false;
  descriptionText: string = 'SHOW.DESC.MSG';
  editView: boolean;
  editPermission: boolean;
  viewPermission: boolean;
  fullName: string;
  email: string;
  profilePic: string;
  userId: string | any;
  isAdmin: string;
  showDeleteStaffModal: boolean = false;
  isDeleteStaff: boolean = false;
  showErrorModal: boolean = false;
  errorPopupProperties: any = {};
  action: string | any;
  staffInfo: IStaffObject;
  @ViewChild('modalConfirmDeleteFooter') modalConfirmDeleteFooter: TemplateRef<any>;

  constructor(
    private _rootScopeService: RootScopeService,
    private _facilityService: FacilityService,
    private _fb: FormBuilder,
    private _router: Router,
    private _route: ActivatedRoute,
    private _staffService: StaffService,
    private _loader: HaloLoaderService,
    private _toast: HaloToastService,
    private _commonUtil: CommonUtil,
    private _modal: HaloModalService,
    private breadcrumbService: BreadcrumbService,
    private _amplitudeAnalytics: AmplitudeAnalyticsService
  ) {}

  getRootScopeData(): void {
    this._rootScopeService.getRootScope().subscribe((rootScopeData) => (this.rootScopeData = rootScopeData));
  }

  setPageName(): void {
    this.rootScopeData.pageName = 'Facility - Staff';
  }

  async ngOnInit() {
    this.getRootScopeData();
    this.setPageName();
    this.setBreadcrumbs(); 
    this.submitted = false;
    this.addStaffForm = this._fb.group({
      email: [
        '',
        Validators.compose([
          Validators.required,
          Validators.pattern(/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+[.][A-Za-z]{2,50}$/),
        ]),
      ],
      //TODO: add a validator to check existing email address once we get list of managers
    });
    this.currentFacility = this._facilityService.selectedFacility();
    this._facilityOnChange = this._facilityService.selectedFacilitySource.subscribe((res) => {      
      this.rootScopeData.isPageHasUnsavedChanges = false; // clear this flag to force navigate to staff list
      this._router.navigateByUrl('/facility/staff'); // redirect to staff page when facility is changed
    });
    this.onChanges();
    this.userId = this._route.snapshot.paramMap.get('staffId');
    await this.getStaffInfo();
  }

  onChanges(): void {
    this.addStaffForm.valueChanges.subscribe((val) => {
      this._hideaction = false;
      this.rootScopeData.isPageHasUnsavedChanges = true;
    });
  }

  ngOnDestroy(): void {
    this._facilityOnChange.unsubscribe();
  }

  setBreadcrumbs() {
    this.action = this._route.snapshot.paramMap.get('action');
    let breadcrumbs = ['FP.USERPROFILE.LBL.LOCATION', 'STAFF'];
    if(this.action === 'view'){
      breadcrumbs.push('VIEW.MY.STAFF.ACCESS');
    } else if(this.action === 'edit'){
      breadcrumbs.push('VIEW.EDIT.STAFF');
    } else {
      breadcrumbs.push('ADD.STAFF');
    } 
    this.breadcrumbService.set(breadcrumbs);
  }

  async getStaffInfo() {
    if (this.currentFacility.facilityId && this.userId) {
      this._loader.startProcess();
      try {
        this.staffInfo = await this._staffService.getStaff(this.currentFacility.facilityId, this.userId);
        this.isAdminChecked = this.staffInfo.isAdmin === 'true';
        this.fullName = this.staffInfo.firstName ? `${this.staffInfo.firstName} ${this.staffInfo.lastName}` : '';
        this.email = this.staffInfo.emailAddress;
        this.profilePic = this.staffInfo.thumbnailUrl;
        this.isAdmin =  this.staffInfo.isAdmin;
      } catch (error) {
        console.error('API failed.');
      }
      this._loader.endProcess();
    }
  }

  async getManagerList() {
    if (this.currentFacility.facilityId) {
      this._loader.startProcess();
      try {
        let data = await this._staffService.getList(this.currentFacility.facilityId).toPromise();
        if(data) {
          this._staffList = data.manager;
        }
      } catch (error) {
        console.error('API failed.');
      }
      this._loader.endProcess();
    }
  }

  onCancel() {
    if (this.rootScopeData.isPageHasUnsavedChanges) {
      this.rootScopeData.showUnSavedChangesModal = true;
      this.rootScopeData.pathToNavigate = '/facility/staff'
    } else {
      this._router.navigate(["/facility/staff"]);
    }
  }

  get addStaffValidator() {
    return this.addStaffForm.controls;
  }

  saveStaffAccess(flag: string): void {
    this.isAdminChecked = flag === 'true';
    if (this.action === 'edit') {
      this._hideaction = false;
    }
    this.rootScopeData.isPageHasUnsavedChanges = true;
  }

  updateUserRights(userRightsData: userPermissions): void {
    this._permissionData = userRightsData;
  }

  async addStaff() {
    this.submitted = true;
    if (this.addStaffForm.dirty && this.addStaffForm.valid) {
      this.emailAlreadyExists = false;
      await this.getManagerList();
      //TODO: need to update the below staff_match check if backend api returns proper error code response for existing email
      let staff_match = this._staffList.filter((staff) => staff.emailAddress.toLocaleLowerCase() === this.addStaffForm.value.email.toLocaleLowerCase());
      if (staff_match.length === 0) {
        this._loader.startProcess();
        let payload = new staffDetails(
          this.currentFacility.facilityId,
          this.addStaffForm.value.email,
          this.isAdminChecked ? 'true' : 'false',
          this._permissionData,
          this.currentFacility.summary.isOptedIn
        );
        this._amplitudeAnalytics.logEvent({elementName: 'Add Staff', action: 'Submit'});
        this._staffService.saveStaff(payload).subscribe(
          (data) => {
            this.rootScopeData.isPageHasUnsavedChanges = false;
            this._loader.endProcess();
            //TODO: need to add amplitude analytics
            //TODO: need to update below code based on proper error code response for existing email
            let param = {
              data: {
                type: data.returnStatus,
                email: this.addStaffForm.value.email,
                facilityName: this.currentFacility.facilityName,
                managerType: this.isAdminChecked ? 'ADMINSTRATOR' : 'STAFF',
              },
            };
            this._router.navigateByUrl('/facility/staff', { state: param });
          },
          (err: any) => {
            this._loader.endProcess();
          }
        );
      } else {
        //TODO: need to remove else if backend api returns proper error code response for existing email
        this.emailAlreadyExists = true;
      }
    }
  }
  editStaff() {
    this._loader.startProcess();
    let updatedPermission = this._permissionData;
    updatedPermission.facilityId = this.currentFacility.facilityId;
    updatedPermission.isAdmin = this.isAdminChecked;
    updatedPermission.userId = this.userId;
    updatedPermission.isOptedIn = this.currentFacility.summary.isOptedIn;
    this._amplitudeAnalytics.logEvent({elementName: 'Edit Staff', action: 'Submit'});
    this._staffService.saveUserPermission(updatedPermission).subscribe(
      (data) => {
        this.rootScopeData.isPageHasUnsavedChanges = false;
        this._router.navigateByUrl('/facility/staff');
        this._loader.endProcess();
        let toastMsg = this.fullName ? `${this.fullName} / ${this.email}` : this.email;
        this._toast.creatToast(this._commonUtil.replaceValueAsHTMLOnTranslatedText('EDITED.SAVED.MSG', [toastMsg]));
      },
      (err: any) => {
        this._loader.endProcess();
      }
    );
  }
  showHideDescription(): void {
    this.isShowDescription = !this.isShowDescription;

    if (this.isShowDescription) {
      this.descriptionText = 'HIDE.DESC.MSG';
    } else {
      this.descriptionText = 'SHOW.DESC.MSG';
    }
  }

  onPermissionChange(event: string) {
    this._hideaction = false;
    this.rootScopeData.isPageHasUnsavedChanges = true;
  }
  deleteStaff() {
    this.showDeleteStaffModal = true;
    this.setDeleteStaffPopupData();

  }
  setDeleteStaffPopupData() {
    this._modal.add({
      dismissible: true,
      titleUppercase: true,
      title: this._commonUtil.translateText('DELETE.STAFF'),
      message: this._commonUtil.translateText(
        'DELETE.MODAL.MESSAGE'
      ),
      footerTemplate: this.modalConfirmDeleteFooter,
    });
  }

  deleteStaffModalConfirmDelete() {
    this._loader.startProcess();
    let deleteStaffDetails = {
      facilityId: this.currentFacility.facilityId,
      managerId: this.userId,
    };
    this._amplitudeAnalytics.logEvent({elementName: 'Delete Staff', action: 'Submit'});
    this._staffService.deleteStaff(deleteStaffDetails).subscribe(
      (data) => {
        this.rootScopeData.isPageHasUnsavedChanges = false;
        this._toast.creatToast(
          this._commonUtil.replaceValueAsHTMLOnTranslatedText('DELETED.SUCCESS.MSG', [
            this.fullName ? this.fullName : this.email,
          ])
        );
        this._modal.close();
        this._router.navigateByUrl('/facility/staff');
        this._loader.endProcess();
      },
      (error) => {
        let errorTitle = error['error'].title;
        this._modal.close();
        if(errorTitle ==='sole_facility') {
          this.errorPopupProperties = {
            title: 'UNABLE.TO.PROCEED',
            message: 'DELETE.STAFF.DELETE.SOLE.ERR',
            isWarningPopup: true,
            ctaButtonLabel: 'OK',
            isCloseIconCallsCta: true,
            hideSecondaryButton: true,
          };
          this.showErrorModal = true;
        } else if (errorTitle === 'invalid_operation') {
          this.errorPopupProperties = {
            title: 'UNABLE.TO.PROCEED',
            message: 'SOLE.STAFF.HEIRARCHY',
            isWarningPopup: true,
            ctaButtonLabel: 'OK',
            isCloseIconCallsCta: true,
            hideSecondaryButton: true,
          };
          this.showErrorModal = true;
        }
        this._loader.endProcess();
      }
    );
  }

  errorModalClose(event: any) {
    this.showErrorModal = false;
  }

  deleteStaffModalCancel() {
    this._modal.close();
  }
}
function typeOf(fullName: string): any {
  throw new Error('Function not implemented.');
}
